import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Award, Users, BarChart3, CheckCircle2, MinusCircle } from "lucide-react";

function Cell({ m }) {
  if (m.passed || m.completed) return <span data-testid="cell-pass" title={m.completed_at ? `Completed ${m.completed_at}` : "Completed"} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="w-4 h-4" /> {m.completed_at || "Completed"}</span>;
  return <span data-testid="cell-none" className="inline-flex items-center gap-1 text-xs text-slate-400"><MinusCircle className="w-4 h-4" /> not completed</span>;
}

export default function ReadinessAnalytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/modules/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;

  return (
    <section data-testid="readiness-analytics" className="mt-10">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Manager report</div>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0A2A5E]">Team readiness</h2>
        <p className="text-sm text-slate-600 mt-1">Who has passed which module across the team · pass mark {data.pass_mark}% · Source: {data.source.source}</p>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Card data-testid="analytics-users" className="p-4 bg-[#0A2A5E] text-white border-0"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-80"><Users className="w-4 h-4" /> People</div><div className="font-display text-3xl font-extrabold mt-2">{data.total_users}</div></Card>
        <Card data-testid="analytics-certified" className="p-4 bg-[#FFF7E8] border border-[#F5A623] text-[#7C3F00]"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-80"><Award className="w-4 h-4" /> LUMIS AI Certified</div><div className="font-display text-3xl font-extrabold mt-2">{data.certified}<span className="text-base font-semibold opacity-70"> / {data.total_users}</span></div></Card>
        <Card data-testid="analytics-passrate" className="p-4 bg-white border-slate-200"><div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-slate-500"><BarChart3 className="w-4 h-4" /> Avg pass rate</div><div className="font-display text-3xl font-extrabold mt-2 text-[#0A2A5E]">{data.modules.length ? Math.round(data.modules.reduce((a, m) => a + m.pass_rate, 0) / data.modules.length) : 0}%</div></Card>
      </div>

      <Card className="bg-white border-slate-200 overflow-hidden mb-4">
        <div className="grid md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          {data.modules.map((m) => (
            <div key={m.id} data-testid={`module-stat-${m.id}`} className="p-4">
              <div className="text-xs font-semibold text-[#0A2A5E] leading-snug line-clamp-2 h-8">{m.title}</div>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#0A2A5E] to-[#1E5AB8]" style={{ width: `${m.pass_rate}%` }} /></div>
              <div className="flex justify-between text-[11px] text-slate-500 mt-1"><span>{m.passed} completed · {m.pass_rate}%</span></div>
            </div>
          ))}
        </div>
      </Card>

      {data.privacy_note && <p data-testid="analytics-privacy-note" className="text-xs text-slate-500">{data.privacy_note}</p>}
      <Card className="bg-white border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-4 py-3">Team member</th>{data.modules.map((m, i) => <th key={m.id} className="px-3 py-3 whitespace-nowrap">Module {i + 1}</th>)}<th className="px-4 py-3">Badges</th><th className="px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {data.people.map((p) => (
              <tr key={p.id} data-testid={`analytics-row-${p.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3"><div className="font-medium text-[#0A2A5E]">{p.name}</div><div className="text-xs text-slate-500">{p.email} · {p.role}</div></td>
                {data.modules.map((m) => <td key={m.id} className="px-3 py-3"><Cell m={p.modules[m.id]} /></td>)}
                <td className="px-4 py-3 font-semibold text-[#0A2A5E]">{p.passed}/{data.modules.length}</td>
                <td className="px-4 py-3">
                  {p.certified ? <span data-testid={`certified-${p.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gradient-to-r from-[#F5A623] to-[#FBD38D] text-[#0A2A5E]"><Award className="w-3.5 h-3.5" /> Certified · {p.certificate_id}</span>
                    : <span className="text-xs text-slate-500">{p.passed ? "In progress" : "Not started"}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </section>
  );
}
